import React, { useState, useEffect } from 'react';
import { Lock, Loader, CheckCircle, ShieldCheck, XCircle, Check } from 'lucide-react';
import { storage } from '../services/storage';

interface SetPasswordProps {
    onSuccess: () => void;
}

export const SetPassword: React.FC<SetPasswordProps> = ({ onSuccess }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const rules = [
        { label: "8 caractères minimum", valid: password.length >= 8 },
        { label: "Une majuscule", valid: /[A-Z]/.test(password) },
        { label: "Une minuscule", valid: /[a-z]/.test(password) },
        { label: "Un caractère spécial", valid: /[!@#$%^&*(),.?":{}|<>_]/.test(password) }, // Added underscore and broad special chars
    ];

    const allValid = rules.every(r => r.valid);
    const match = password && password === confirmPassword;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        if (!allValid) {
            setError("Le mot de passe ne respecte pas les critères de sécurité.");
            return;
        }

        if (!match) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        setLoading(true);
        try {
            const { error } = await storage.updateUserPassword(password);
            if (error) throw error;
            onSuccess();
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Erreur lors de la mise à jour.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 animate-in fade-in zoom-in-95 duration-300">
                <div className="text-center mb-8">
                    <div className="bg-amber-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-sm border-4 border-white">
                        <ShieldCheck className="w-10 h-10 text-amber-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Définition du mot de passe</h2>
                    <p className="text-slate-500 mt-2 text-sm">Veuillez choisir un mot de passe sécurisé pour accéder à votre espace.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nouveau mot de passe</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
                            <input 
                                type="password" 
                                required
                                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none bg-white text-slate-900 transition-all"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        
                        {/* Rules Feedback */}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            {rules.map((rule, idx) => (
                                <div key={idx} className={`flex items-center text-xs ${rule.valid ? 'text-green-600' : 'text-slate-400'}`}>
                                    <div className={`w-3 h-3 rounded-full flex items-center justify-center mr-1.5 ${rule.valid ? 'bg-green-100' : 'bg-slate-100 border border-slate-200'}`}>
                                        {rule.valid && <Check className="w-2 h-2" />}
                                    </div>
                                    {rule.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Confirmer le mot de passe</label>
                        <div className="relative">
                            <CheckCircle className={`absolute left-3 top-2.5 w-5 h-5 ${match && password ? 'text-green-500' : 'text-slate-400'}`} />
                            <input 
                                type="password" 
                                required
                                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 outline-none bg-white text-slate-900 transition-all ${match && password ? 'border-green-300 focus:ring-green-500' : 'border-slate-300 focus:ring-amber-500'}`}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-100 flex items-start">
                            <XCircle className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
                            {error}
                        </div>
                    )}

                    <button 
                        type="submit"
                        disabled={loading || !allValid || !match}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg transition-colors shadow-lg shadow-slate-900/20 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? <Loader className="animate-spin w-5 h-5" /> : "Enregistrer et accéder"}
                    </button>
                </form>
            </div>
        </div>
    );
};